import { z } from 'zod';

import { getPlaceDetails } from '@services/places';

const apiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

// Returns a place shaped like the legacy `PlacesService.getDetails()` result so
// `parseAddressComponents` below keeps working unchanged — see `@services/places`.
export const getGooglePlaceDetails = (placeId) => getPlaceDetails(placeId, { apiKey });

export const parseOpeningHours = (opening_hours) => {
	if (!opening_hours || !opening_hours.periods) return undefined;

	try {
		const formatHours = opening_hours.periods.map((hour) => {
			if (!hour.close) return null;
			const {
				close: { day, time }
			} = hour;
			const { open } = hour;
			const openingTime = open.time.replace(/\b(\d{1,2})(\d{2})/g, '$1:$2');
			const closingTime = time.replace(/\b(\d{1,2})(\d{2})/g, '$1:$2');
			return {
				day,
				close: closingTime,
				open: openingTime
			};
		});
		return formatHours.filter(Boolean);
	} catch (error) {
		console.error('Error parsing opening hours:', error);
		return undefined;
	}
};

export const parseAddressComponents = (placeDetails, googlePlaceId) => {
	const components = placeDetails.address_components || [];
	const addressParts = placeDetails.formatted_address ? placeDetails.formatted_address.split(', ') : [];

	const getComponentsString = (chosenTypes, short = false) =>
		components
			.filter(({ types }) => types.some((type) => chosenTypes.includes(type)))
			.map(({ long_name, short_name }) => (short ? short_name : long_name))
			.join(' ');

	const addressLine1 = addressParts[0] || getComponentsString(['street_number', 'street', 'street_address', 'route']);
	const postcode = getComponentsString(['postal_code']);
	const addressLine2 = addressParts.length > 1 ? addressParts[1].split(' ' + postcode)[0] : undefined;

	return {
		name: placeDetails.name,
		addressLine1,
		addressLine2,
		postcode,
		city: getComponentsString(['postal_town', 'locality']),
		country: getComponentsString(['country'], true),
		publicPhoneNumber: placeDetails.formatted_phone_number,
		geolocation: {
			lat: placeDetails.geometry.location.lat(),
			lng: placeDetails.geometry.location.lng()
		},
		googlePlaceId,
		types: placeDetails.types || [],
		openingHours: parseOpeningHours(placeDetails.opening_hours)
	};
};

const emailSchema = z.string().email();

export const validateEmail = (email) => {
	try {
		emailSchema.parse(email);
		return true;
	} catch {
		return false;
	}
};

const formDataSchema = z.object({
	name: z.string().min(1, 'Name is required').trim(),
	email: z.string().min(1, 'Email is required').email('Invalid email format')
});

const addressDetailsSchema = z.object({
	addressLine1: z.string().min(1, 'Please select a store location'),
	postcode: z.string().min(1, 'Postcode is required'),
	country: z.string().min(1, 'Country is required')
});

export const validateForm = (formData, addressDetails) => {
	const errors = {};

	const formValidation = formDataSchema.safeParse(formData);
	if (!formValidation.success) {
		formValidation.error.issues.forEach((err) => {
			errors[err.path[0]] = err.message;
		});
	}

	const addressValidation = addressDetailsSchema.safeParse(addressDetails);
	if (!addressValidation.success) {
		addressValidation.error.issues.forEach((err) => {
			const field = err.path[0];
			if (field === 'addressLine1') {
				errors.address = err.message;
			} else {
				errors[field] = err.message;
			}
		});
	}

	return errors;
};
